/**
 * 引擎主入口：`startHand` / `applyAction` / `replayHand`。
 * 见 docs/ARCHITECTURE.md §4.9 / §4.11 / §4.14 / §4.15。
 *
 * 三条贯穿本文件的规则：
 *
 *  - 红线 #16：**纯函数**。不读写全局、不做 I/O、不调 `Date.now()`、不调 RNG。
 *    唯一的随机入口是调用方注入的 `deck`（由 `shuffleDeck(rng)` 预先洗好）。
 *  - 红线 #20：提前结束的判据是 `status !== 'folded'`，**不是** `=== 'active'`。
 *    全下玩家仍是参与者——A 全下、B 与 C 对打、C 弃牌，仍必须跑完公共牌摊牌。
 *  - 红线 #3：`endedBy === 'fold'` 时 `showdown` 恒为空数组，赢家永不亮牌。
 *
 * `applyAction` 一路推进到**下一个需要玩家决策的点**（或手牌结束），调用方无需反复 pump；
 * 所有中间过程以事件返回，服务端按 `pacingHintMs` 的节奏广播。
 */
import type { CardId, Chips, HandId, SeatIndex } from '../ids.js'
import { SEAT_LIMITS } from '../config/roomConfig.js'
import {
  deckVariantOf,
  HAND_STATE_SCHEMA_VERSION,
  InvariantError,
  engineErr,
  engineOk,
  isBettingPhase,
} from './types.js'
import type {
  ActionRequest,
  AppliedAction,
  AppliedActionType,
  EngineResult,
  HandConfig,
  HandEvent,
  HandPlayer,
  HandRank,
  HandResult,
  HandState,
  Pot,
  PotAward,
  ShowdownEntry,
  Street,
} from './types.js'
import { isValidDeck } from './deck.js'
import { evaluateHand } from './evaluator.js'
import {
  isBettingRoundComplete,
  legalActions,
  nextToAct,
  validateAction,
} from './betting.js'
import {
  buildPots,
  distributePots,
  seatsClockwiseFrom,
  computeUncalledBet,
  wonBySeat,
} from './pots.js'
import type { PotContributor } from './pots.js'
import { assignSeats, positionsForHand } from './seating.js'
import type { TableSeatSnapshot } from './seating.js'

// ============================================================================
// 对外形状
// ============================================================================

export interface StartHandInput {
  readonly config: HandConfig
  readonly seats: readonly TableSeatSnapshot[]
  readonly prevBigBlindSeat: SeatIndex | null
  /**
   * 上一手 complete 的小盲**位置**（`HandState.smallBlindPosition`）。死按钮靠它定按钮；
   * null = 没有记录（第一手 / 老数据）。voided 的手不推进它（红线 #25）。
   */
  readonly prevSmallBlindSeat: SeatIndex | null
  /** 按一张新桌子开（忽略入局标记）。见 `AssignSeatsOptions.freshTable` */
  readonly freshTable?: boolean
  /** 预洗好的牌堆（标准 52 张，短牌 36 张，见 config.deckVariant）。引擎内部不调用任何 RNG（铁律 #2） */
  readonly deck: readonly CardId[]
  /** 房间内的手牌序号，只用于 `HandResult.handNo` 与 `handStarted` 事件；缺省 0 */
  readonly handNo?: number
}

export interface ActionResult {
  readonly state: HandState
  readonly events: readonly HandEvent[]
}

/**
 * `HandState` 没有 `handNo` 槽位，但 `HandResult.handNo` 与 `handStarted` 事件都要它。
 * 引擎把它挂在 state 上随手牌流转（`cloneState` 会原样带走），并对缺失情况降级为 0，
 * 于是外部构造的 `HandState`（测试夹具、旧快照）也能安全喂进 `applyAction`。
 */
export interface EngineHandState extends HandState {
  readonly handNo: number
}

/** §4.9：跑马时各街的广播节奏提示。状态本身不等待动画。 */
export const PACING_HINT_MS: Readonly<Record<Street, number>> = {
  preflop: 0,
  flop: 900,
  turn: 800,
  river: 800,
}

const STREETS: readonly Street[] = ['preflop', 'flop', 'turn', 'river']

// ============================================================================
// 内部小工具
// ============================================================================

function playersOf(state: HandState): HandPlayer[] {
  return Object.values(state.players)
}

function seatOf(state: HandState, seat: SeatIndex): HandPlayer {
  const p = state.players[seat]
  if (p === undefined) throw new InvariantError(`engine: 座位 ${String(seat)} 不在本手牌中`)
  return p
}

function handNoOf(state: HandState): number {
  const v = (state as unknown as Record<string, unknown>)['handNo']
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : 0
}

/** `potTotal` 恒为 Σ committedThisHand（退还后）。见 §4.10：pots 只在街末重建，不能拿它当分母。 */
function syncPotTotal(draft: HandState): void {
  let sum = 0
  for (const p of playersOf(draft)) sum += p.committedThisHand
  draft.potTotal = sum
}

/**
 * 白名单逐字段克隆（铁律 #3：入参 state 视为 immutable，只能改自己 clone 出来的 draft）。
 * 刻意不用 `structuredClone`：`deck` / `config` / `pots` / `showdown` 全手不可变，
 * 深拷它们纯属浪费；而漏拷 `players` 里的任何一个会让引擎改写调用方的历史快照。
 */
function cloneState(s: HandState): HandState {
  const players: Record<SeatIndex, HandPlayer> = {}
  for (const p of playersOf(s)) players[p.seat] = { ...p }
  const next: EngineHandState = {
    schemaVersion: s.schemaVersion,
    config: s.config,
    handNo: handNoOf(s),
    buttonSeat: s.buttonSeat,
    smallBlindSeat: s.smallBlindSeat,
    bigBlindSeat: s.bigBlindSeat,
    ...(s.smallBlindPosition === undefined ? {} : { smallBlindPosition: s.smallBlindPosition }),
    isHeadsUp: s.isHeadsUp,
    deadButton: s.deadButton,
    dealtSeats: s.dealtSeats,
    players,
    deck: s.deck,
    deckIndex: s.deckIndex,
    burned: s.burned.slice(),
    board: s.board.slice(),
    phase: s.phase,
    street: s.street,
    betToMatch: s.betToMatch,
    lastFullRaiseSize: s.lastFullRaiseSize,
    lastAggressorSeatThisStreet: s.lastAggressorSeatThisStreet,
    actionOn: s.actionOn,
    potTotal: s.potTotal,
    pots: s.pots,
    actionLog: s.actionLog.slice(),
    uncalledReturn: s.uncalledReturn,
    showdown: s.showdown,
    result: s.result,
  }
  return next
}

// ============================================================================
// 下注轮开闭（§4.7）
// ============================================================================

function resetBettingRound(draft: HandState, street: Street): void {
  draft.street = street
  draft.betToMatch = street === 'preflop' ? draft.config.bigBlind : 0
  // 红线 #23：每条街都回到 BB，否则翻牌圈的最小下注会退化成上一条街的加注量
  draft.lastFullRaiseSize = draft.config.bigBlind
  draft.lastAggressorSeatThisStreet = null
  for (const p of playersOf(draft)) {
    if (street !== 'preflop') p.committedThisRound = 0
    p.hasActedThisRound = false
    p.lastAction = null
  }
}

function openBettingRound(draft: HandState, street: Street): void {
  resetBettingRound(draft, street)
  draft.phase = street
  // 红线 #22：行动权推进一律走 nextToAct。预计算的首位行动者很可能已被 ante 打成全下。
  const startSeat = street === 'preflop' ? draft.bigBlindSeat : draft.buttonSeat
  draft.actionOn = nextToAct(draft, startSeat + 1)
}

// ============================================================================
// 强制投入（ante / 死钱 / 盲注）
// ============================================================================

function forcedAction(
  draft: HandState,
  p: HandPlayer,
  type: AppliedActionType,
  delta: Chips,
  countsInRound: boolean,
): AppliedAction {
  let potBefore = 0
  let activeMask = 0
  let allInMask = 0
  for (const o of playersOf(draft)) {
    potBefore += o.committedThisHand
    if (o.status === 'active') activeMask |= 1 << o.seat
    else if (o.status === 'allIn') allInMask |= 1 << o.seat
  }
  return {
    index: draft.actionLog.length,
    street: 'preflop',
    seat: p.seat,
    type,
    // 红线 #18：`to` 恒为本轮累计投入总额。ante / 死钱不计入本轮，故 `to` 原地不动。
    to: countsInRound ? p.committedThisRound + delta : p.committedThisRound,
    delta,
    allIn: delta >= p.stack,
    reopenedAction: false,
    source: 'admin',
    potBefore,
    stackBefore: p.stack,
    streetInvestedBefore: p.committedThisRound,
    toCallBefore: 0,
    minRaiseToBefore: null,
    maxRaiseToBefore: p.committedThisRound + p.stack,
    betsBefore: 1, // §12.3：翻前大盲注本身算第 1 个下注
    activeMask,
    allInMask,
  }
}

/**
 * 收一笔强制投入。`countsInRound=false` 的 ante / 死钱只进 `committedThisHand`
 * 与底池，**不进** `committedThisRound`——所以它们不抬高 `betToMatch`、不改变
 * min-raise 基准（§4.13）。四种强制投入都**不置** `hasActedThisRound`，
 * 于是全员 limp 到大盲时，大盲自动拿到 check/raise 选项（§4.6「大盲 option 不特判」）。
 */
function postForced(
  draft: HandState,
  seat: SeatIndex,
  type: AppliedActionType,
  kind: 'sb' | 'bb' | 'ante' | 'dead',
  amount: Chips,
  events: HandEvent[],
  countsInRound: boolean,
): void {
  const p = seatOf(draft, seat)
  const delta = Math.min(Math.max(0, amount), p.stack)
  if (delta === 0) return

  const action = forcedAction(draft, p, type, delta, countsInRound)
  p.stack -= delta
  p.committedThisHand += delta
  if (countsInRound) p.committedThisRound += delta
  if (p.stack === 0) p.status = 'allIn'
  p.lastAction = type
  draft.actionLog.push(action)
  syncPotTotal(draft)
  events.push({ t: 'blindPosted', seat, kind, amount: delta, allIn: p.stack === 0 })
}

// ============================================================================
// 发牌（§4.15）
// ============================================================================

function drawCard(draft: HandState): CardId {
  const c = draft.deck[draft.deckIndex]
  if (c === undefined) throw new InvariantError(`engine: 牌堆耗尽（deckIndex=${draft.deckIndex}）`)
  draft.deckIndex += 1
  return c
}

function dealStreet(draft: HandState, street: Street, events: HandEvent[]): void {
  if (draft.config.burnCards) draft.burned.push(drawCard(draft))
  const count = street === 'flop' ? 3 : 1
  const cards: CardId[] = []
  for (let i = 0; i < count; i++) {
    const c = drawCard(draft)
    draft.board.push(c)
    cards.push(c)
  }
  draft.street = street
  events.push({ t: 'streetDealt', street, cards, pacingHintMs: PACING_HINT_MS[street] })
}

function streetsAfter(boardLength: number): Street[] {
  if (boardLength >= 5) return []
  if (boardLength >= 4) return ['river']
  if (boardLength >= 3) return ['turn', 'river']
  return ['flop', 'turn', 'river']
}

function nextStreetOf(street: Street): Street {
  const i = STREETS.indexOf(street)
  const next = STREETS[i + 1]
  if (next === undefined) throw new InvariantError(`engine: ${street} 之后没有下一条街`)
  return next
}

// ============================================================================
// 街末结算（§4.8 afterBettingRoundComplete）
// ============================================================================

/**
 * 未跟注退还（§4.8 步骤一），**按本轮投入计算**。
 *
 * ⚠️ 与规格的偏离（规格写的是 `committedThisHand`，那是错的）：
 * ante 与死钱按 §4.13 只进 `committedThisHand`、不进 `committedThisRound`——它们是
 * 死钱，「被不被跟注」这个问题对它们根本不成立。用整手口径算「最高 − 次高」时，
 * `anteMode: 'bigBlindAnte'` 下大盲的 `committedThisHand` 恒比所有人高出一个 ante，
 * 于是每手翻前一结束就把 ante 原样退回给大盲，底池凭空少掉一份 ante；
 * `missedBlindPolicy: 'postOnReturn'` 的死钱同理，还会反向把别人该拿的退还吃掉
 * （死钱让补盲者的整手投入追平大盲，excess 被抹成 0）。
 * 本轮口径天然排除死钱，且规格那句「弃牌者的投入正是他已匹配的金额」在本轮口径下
 * 依然成立，所以公式不变、只换基准。
 *
 * 这同时修复了规格引理「一手至多触发一次非零退还，此后不再有下注轮」——
 * 整手口径下 bigBlindAnte 每手翻前都退一次，翻牌圈照样有三个人在打。
 *
 * 落账的三处收尾：
 * ① `committedThisHand` / `committedThisRound` 同步冲减：退还的钱不进 `contributed`
 *    （红线 #19），也不能继续画在台面上。
 * ② 退还可能把一个「全下」玩家的筹码又推回正数——大盲 8 全下、小盲只有 3 全下时，
 *    大盲那 5 枚从来没被匹配过。此时他事实上并没有全下，status 必须改回 active，
 *    否则不变量 3（allIn ⟺ stack === 0）当场破。
 * ③ 幂等：调用方负责只在 `uncalledReturn === null` 时调用（payout 的兜底）。
 */
function settleUncalledThisRound(draft: HandState, events: HandEvent[]): void {
  const ret = computeUncalledBet(
    playersOf(draft).map((p) => ({
      seat: p.seat,
      stack: p.stack,
      committedThisHand: p.committedThisRound,
    })),
  )
  if (ret === null) return
  const p = seatOf(draft, ret.seat)
  p.stack += ret.amount
  p.committedThisHand -= ret.amount
  p.committedThisRound -= ret.amount
  if (p.stack > 0 && p.status === 'allIn') p.status = 'active'
  draft.uncalledReturn = ret
  events.push({ t: 'uncalledReturned', seat: ret.seat, amount: ret.amount })
}

/**
 * 切池。绝大多数情况直接走 `buildPots`，但有一种局面它的分层模型表达不了：
 * **池子里只剩死钱，而唯一还没弃牌的人一分钱都没留在池里**。
 *
 * 复现：`missedBlindPolicy: 'postOnReturn'` 的补盲者交了一份等于 BB 的死钱后弃牌，
 * 本手又恰好是 dead small blind（没有小盲），于是大盲那注盲注无人跟，整额被退还，
 * 大盲的 `committedThisHand` 归零。此时 `buildPots` 的最低一层里只有弃牌者，
 * `eligibleSeats` 为空且下方没有池可以接住，它会抛 InvariantError 把整手牌炸掉。
 *
 * 正确的扑克裁决是「死钱归最后站着的人」——死钱本来就不属于任何人的下注，
 * 不该因为赢家自己没往池里留钱就无处可去。这里直接构造那个唯一的底池。
 * 能走到这个分支必然只剩一个未弃牌者（退还至多发生在一个人身上，其余未弃牌者
 * 若还在场就一定留了钱在池里），`distributePots` 会把它整额判给他。
 */
function rebuildPots(draft: HandState): readonly Pot[] {
  const all = playersOf(draft)
  const live = all.filter((p) => p.status !== 'folded')
  const total = all.reduce((sum, p) => sum + p.committedThisHand, 0)
  if (total > 0 && live.length > 0 && !live.some((p) => p.committedThisHand > 0)) {
    return [
      {
        index: 0,
        amount: total,
        eligibleSeats: live.map((p) => p.seat).sort((a, b) => a - b),
        // capLevel 描述「eligible 座位投够了多少」——他们一分钱都没投
        capLevel: 0,
      },
    ]
  }
  return buildPots(potContributors(draft))
}

/**
 * `buildPots` 的入参视图：把 `bigBlindAnte` 的那笔 ante 标成死钱。
 *
 * 大盲替**全桌**缴的 ante 没有任何对手需要匹配，它一进池就属于本手的赢家。
 * 若照常按 `committedThisHand` 分层，大盲恒比别人高出一个 ante，这笔钱每手都会
 * 被切成「只有大盲有资格」的私人边池原路返回——别人赢下这手也拿不到 ante，
 * `bigBlindAnte` 整个模式退化成空转。见 `pots.ts` 的 `PotContributor.deadMoney`。
 *
 * 补盲时交的死小盲（`postDead`）同理：它不算本人的下注，放进底池中间归赢家。
 * 以前没标，补盲者一路过牌到摊牌输掉时，这笔钱会被切成他自己的边池原路退回。
 *
 * `each` 模式**不算**死钱：人人各缴一份，短码只缴得起一部分也就只配赢到那个档位，
 * 照常分层才对（§4.13）。实缴额从 `actionLog` 取而不是取 `config.ante`——
 * 大盲筹码不够一个 ante 时 `postForced` 会把它钳到 stack。
 */
function potContributors(draft: HandState): PotContributor[] {
  const dead: Record<SeatIndex, Chips> = {}
  const anteIsDead = draft.config.anteMode === 'bigBlindAnte'
  for (const a of draft.actionLog) {
    if (a.type === 'postDead' || (anteIsDead && a.type === 'postAnte')) {
      dead[a.seat] = (dead[a.seat] ?? 0) + a.delta
    }
  }
  return playersOf(draft).map((p) => ({
    seat: p.seat,
    committedThisHand: p.committedThisHand,
    status: p.status,
    deadMoney: dead[p.seat] ?? 0,
  }))
}

function closeBettingRound(draft: HandState, events: HandEvent[]): void {
  settleUncalledThisRound(draft, events)
  draft.pots = rebuildPots(draft)
  syncPotTotal(draft)
  events.push({ t: 'potsUpdated', pots: draft.pots, potTotal: draft.potTotal })
}

// ============================================================================
// 摊牌（§4.11）
// ============================================================================

function showdownOrder(draft: HandState, stillIn: readonly SeatIndex[]): SeatIndex[] {
  const maxSeats = draft.config.maxSeats
  const agg = draft.lastAggressorSeatThisStreet
  let start: SeatIndex
  if (agg !== null && stillIn.includes(agg)) {
    start = agg
  } else {
    // 本街无人主动下注 → 按钮左手第一个未弃牌者先亮
    const first = seatsClockwiseFrom(draft.buttonSeat + 1, maxSeats).find((s) =>
      stillIn.includes(s),
    )
    if (first === undefined) throw new InvariantError('engine: 摊牌时找不到起始座位')
    start = first
  }
  return seatsClockwiseFrom(start, maxSeats).filter((s) => stillIn.includes(s))
}

function ranksFor(draft: HandState, seats: readonly SeatIndex[]): Record<SeatIndex, HandRank> {
  const out: Record<SeatIndex, HandRank> = {}
  for (const seat of seats) {
    const p = seatOf(draft, seat)
    out[seat] = evaluateHand([...p.holeCards, ...draft.board], deckVariantOf(draft.config))
  }
  return out
}

/**
 * §4.11。`awards` **必须先于**本函数算出：`distributePots` 只依赖 `ranksBySeat`，
 * 与谁亮不亮牌无关。原规格的「单调扫描 muck」在多边池下是错的——一个玩家可能输掉
 * 主池却赢下某个边池，被前面的「当前最佳者」压住却仍必须亮牌去争自己 eligible 的池。
 */
function computeShowdown(
  draft: HandState,
  awards: readonly PotAward[],
  ranks: Readonly<Record<SeatIndex, HandRank>>,
  stillIn: readonly SeatIndex[],
): ShowdownEntry[] {
  const forced = new Set<SeatIndex>()
  for (const seat of stillIn) {
    const p = seatOf(draft, seat)
    if (p.status === 'allIn') forced.add(seat) // 全下者无权盖牌
    if (p.hasShownCards) forced.add(seat) // 跑马时已经亮过，收不回去
  }
  for (const a of awards) forced.add(a.seat) // 赢任一池者必须亮
  if (draft.config.showdownPolicy === 'revealAll') for (const seat of stillIn) forced.add(seat)

  const order = showdownOrder(draft, stillIn)
  const bestByPot = new Map<number, number>()
  const entries: ShowdownEntry[] = []

  for (let i = 0; i < order.length; i++) {
    const seat = order[i] as SeatIndex
    const rank = ranks[seat]
    if (rank === undefined) throw new InvariantError(`engine: 摊牌缺少 seat ${seat} 的牌力`)

    let mucked = false
    if (!forced.has(seat)) {
      const myPots = draft.pots.filter((pot) => pot.eligibleSeats.includes(seat))
      // 只有在他 eligible 的**每一个**池里都已被已亮牌者超过，才允许盖牌
      mucked =
        myPots.length > 0 &&
        myPots.every((pot) => {
          const best = bestByPot.get(pot.index)
          // HandRankValue 是数值编码，直接比大小；红线 #28 针对的是字符串枚举
          return best !== undefined && rank.value <= best
        })
    }

    if (mucked) {
      // 判别联合必须先分支再构造：先建对象后补字段会让 { mucked:true, rank } 这种泄漏结构成立
      entries.push({ seat, mucked: true, holeCards: null, rank: null, revealOrder: i })
      continue
    }
    entries.push({
      seat,
      mucked: false,
      holeCards: seatOf(draft, seat).holeCards,
      rank,
      revealOrder: i,
    })
    for (const pot of draft.pots) {
      if (!pot.eligibleSeats.includes(seat)) continue
      const cur = bestByPot.get(pot.index)
      if (cur === undefined || rank.value > cur) bestByPot.set(pot.index, rank.value)
    }
  }
  return entries
}

// ============================================================================
// 跑马（§4.9 run it out）
// ============================================================================

function emitReveals(draft: HandState, seats: readonly SeatIndex[], events: HandEvent[]): void {
  const ranks = ranksFor(draft, seats)
  for (const seat of seats) {
    const p = seatOf(draft, seat)
    const rank = ranks[seat]
    if (rank === undefined) continue
    events.push({ t: 'cardsRevealed', seat, cards: p.holeCards, rank, voluntary: false })
  }
}

/**
 * 可行动者 < 2 时一次性跑完剩余公共牌。
 *
 * **全下玩家的牌必须在发剩余公共牌之前摊开**——这是真实规则，也让新手辅助在跑马阶段
 * 有意义。状态层的「摊开」（`hasShownCards = true`）严格先于任何一张牌被发出，
 * 于是跑马中途拿到的快照一定已经能看到底牌。
 *
 * ⚠️ 与规格的一处偏离：`cardsRevealed` 事件带完整 `HandRank`，而 `rankCards` 只吃 5~7 张。
 * 翻前全下时 `hole + board` 只有 2 张，算不出牌力。此时把 `cardsRevealed` 推迟到翻牌
 * 发出之后（第一个能算出牌力的时刻）；**绝不**改用最终 7 张来算——那等于把还没广播的
 * 转牌/河牌塞进 `rank.bestFive` 提前泄露给客户端。
 */
function runItOut(draft: HandState, events: HandEvent[]): void {
  const stillIn = playersOf(draft)
    .filter((p) => p.status !== 'folded')
    .map((p) => p.seat)
  const order = showdownOrder(draft, stillIn)
  for (const seat of order) seatOf(draft, seat).hasShownCards = true

  // 先切出 showdown 相位再发牌：留在下注相位会让不变量 7（Σpots 对账）在
  // committedThisRound 尚未清零的窗口里误报。
  draft.phase = 'showdown'
  draft.actionOn = null

  const remaining = streetsAfter(draft.board.length)
  let revealed = false
  if (draft.board.length >= 3) {
    emitReveals(draft, order, events)
    revealed = true
  }
  events.push({ t: 'runItOut', remainingStreets: remaining })
  for (const street of remaining) {
    dealStreet(draft, street, events)
    if (!revealed && draft.board.length >= 3) {
      emitReveals(draft, order, events)
      revealed = true
    }
  }
}

// ============================================================================
// 状态机推进（§4.9）
// ============================================================================

function runShowdown(draft: HandState, events: HandEvent[]): readonly PotAward[] | null {
  const stillIn = playersOf(draft)
    .filter((p) => p.status !== 'folded')
    .map((p) => p.seat)
  // 红线 #3：只剩一人未弃牌根本不进摊牌，赢家永不亮牌
  if (stillIn.length <= 1) return null

  const ranks = ranksFor(draft, stillIn)
  const awards = distributePots(draft.pots, ranks, draft.buttonSeat, draft.config.maxSeats)
  const entries = computeShowdown(draft, awards, ranks, stillIn)
  draft.showdown = entries

  for (const e of entries) {
    if (e.mucked) {
      events.push({ t: 'cardsMucked', seat: e.seat })
      continue
    }
    seatOf(draft, e.seat).hasShownCards = true
    events.push({
      t: 'cardsRevealed',
      seat: e.seat,
      cards: e.holeCards,
      rank: e.rank,
      voluntary: false,
    })
  }
  return awards
}

function runPayout(
  draft: HandState,
  events: HandEvent[],
  precomputed: readonly PotAward[] | null,
): void {
  // ① 幂等兜底（§4.8）：一手牌至多触发一次非零退还，重复调用安全
  if (draft.uncalledReturn === null) settleUncalledThisRound(draft, events)
  if (draft.pots.length === 0) {
    draft.pots = rebuildPots(draft)
    syncPotTotal(draft)
    events.push({ t: 'potsUpdated', pots: draft.pots, potTotal: draft.potTotal })
  }

  const stillIn = playersOf(draft).filter((p) => p.status !== 'folded')
  const endedBy: 'fold' | 'showdown' = stillIn.length <= 1 ? 'fold' : 'showdown'
  const raw =
    precomputed ?? distributePots(draft.pots, {}, draft.buttonSeat, draft.config.maxSeats)
  // 红线 #3：fold 结束时 awards[*].handRank 全为 null
  const awards: PotAward[] =
    endedBy === 'fold' ? raw.map((a) => ({ ...a, handRank: null })) : raw.slice()

  // ② 赢家入账
  for (const a of awards) {
    seatOf(draft, a.seat).stack += a.amount
    events.push({ t: 'potAwarded', award: a })
  }

  // ③ HandResult（§4.18）：手牌历史与统计的自足单元
  const startingStacks: Record<SeatIndex, Chips> = {}
  const contributedBySeat: Record<SeatIndex, Chips> = {}
  const wonAll: Record<SeatIndex, Chips> = {}
  const netBySeat: Record<SeatIndex, number> = {}
  const finalStacks: Record<SeatIndex, Chips> = {}
  const won = wonBySeat(awards)
  for (const p of playersOf(draft)) {
    startingStacks[p.seat] = p.startingStack
    // 红线 #19：contributed 不含退还、won 不含退还
    contributedBySeat[p.seat] = p.committedThisHand
    const w = won[p.seat] ?? 0
    wonAll[p.seat] = w
    netBySeat[p.seat] = w - p.committedThisHand
    finalStacks[p.seat] = p.stack
  }

  const showdown: readonly ShowdownEntry[] = endedBy === 'fold' ? [] : draft.showdown
  const result: HandResult = {
    handId: draft.config.handId,
    handNo: handNoOf(draft),
    endedBy,
    buttonSeat: draft.buttonSeat,
    smallBlindSeat: draft.smallBlindSeat,
    bigBlindSeat: draft.bigBlindSeat,
    startingStacks,
    actionLog: draft.actionLog.slice(),
    board: draft.board.slice(),
    pots: draft.pots,
    awards,
    uncalledReturn: draft.uncalledReturn,
    showdown,
    contributedBySeat,
    wonBySeat: wonAll,
    netBySeat,
    finalStacks,
  }
  draft.showdown = showdown
  draft.result = result
  draft.actionOn = null
  draft.phase = 'complete'
  events.push({ t: 'handComplete', result })
}

/** 一路推进到下一个需要玩家决策的点（或手牌结束）。 */
function advance(draft: HandState, events: HandEvent[]): void {
  let awards: readonly PotAward[] | null = null

  for (let guard = 0; guard < 64; guard++) {
    if (draft.phase === 'complete') return
    if (draft.phase === 'showdown') {
      awards = runShowdown(draft, events)
      draft.phase = 'payout'
      continue
    }
    if (draft.phase === 'payout') {
      runPayout(draft, events, awards)
      continue
    }
    if (!isBettingPhase(draft.phase)) {
      throw new InvariantError(`advance: 意外的阶段 ${draft.phase}`)
    }

    // —— ① 只剩一人未弃牌 → 提前结束（红线 #20：判据是 !== 'folded'）——
    const contenders = playersOf(draft).filter((p) => p.status !== 'folded')
    if (contenders.length <= 1) {
      closeBettingRound(draft, events)
      draft.actionOn = null
      draft.phase = 'payout'
      continue
    }

    // —— ② 本轮还没打完 → 交还行动权 ——
    if (!isBettingRoundComplete(draft)) {
      const seat = draft.actionOn
      if (seat === null) throw new InvariantError('advance: 下注轮未结束却无人持有行动权')
      events.push({ t: 'actionOn', seat, legal: legalActions(draft, seat) })
      return
    }

    // —— ③ 本轮打完 ——
    closeBettingRound(draft, events)
    draft.actionOn = null

    const canStillAct = playersOf(draft).filter(
      (p) => p.status === 'active' && p.stack > 0,
    ).length
    if (canStillAct < 2 && draft.board.length < 5) {
      runItOut(draft, events) // 内部已把 phase 切到 showdown
      continue
    }
    if (draft.phase === 'river') {
      draft.phase = 'showdown'
      continue
    }
    const next = nextStreetOf(draft.phase)
    dealStreet(draft, next, events)
    openBettingRound(draft, next)
  }
  throw new InvariantError('advance: 状态机未在有限步内收敛')
}

// ============================================================================
// startHand（§4.14 输入校验清单）
// ============================================================================

function validateConfig(config: HandConfig): EngineResult<null> {
  const { maxSeats, smallBlind, bigBlind, ante } = config
  if (typeof config.handId !== 'string' || config.handId.length === 0) {
    return engineErr('INVALID_CONFIG', 'handId 不能为空')
  }
  if (!Number.isInteger(maxSeats) || maxSeats < SEAT_LIMITS.MIN || maxSeats > SEAT_LIMITS.MAX) {
    return engineErr('INVALID_CONFIG', `maxSeats 越界: ${String(maxSeats)}`)
  }
  // 红线 #17：筹码全是整数
  if (!Number.isInteger(bigBlind) || bigBlind < 2) {
    return engineErr('INVALID_CONFIG', `bigBlind 必须是 >= 2 的整数: ${String(bigBlind)}`)
  }
  if (!Number.isInteger(smallBlind) || smallBlind < 1) {
    return engineErr('INVALID_CONFIG', `smallBlind 必须是 >= 1 的整数: ${String(smallBlind)}`)
  }
  if (smallBlind > bigBlind) {
    return engineErr('INVALID_CONFIG', `smallBlind ${smallBlind} > bigBlind ${bigBlind}`)
  }
  if (!Number.isInteger(ante) || ante < 0) {
    return engineErr('INVALID_CONFIG', `ante 必须是非负整数: ${String(ante)}`)
  }
  // 玩法来自存档 / 客户端，取值必须可信；缺省按标准
  if (config.deckVariant !== undefined && config.deckVariant !== 'standard' && config.deckVariant !== 'shortDeck') {
    return engineErr('INVALID_CONFIG', `未知玩法: ${String(config.deckVariant)}`)
  }
  return engineOk(null)
}

export function startHand(input: StartHandInput): EngineResult<ActionResult> {
  const config = input.config
  const cfg = validateConfig(config)
  if (!cfg.ok) return cfg
  const variant = deckVariantOf(config)
  if (!isValidDeck(input.deck, variant)) {
    return engineErr(
      'INVALID_DECK',
      variant === 'shortDeck' ? '短牌牌堆必须是 36 张互异的 6-A' : '牌堆必须是 52 张互异的合法 CardId',
    )
  }

  const assigned = assignSeats(input.seats, input.prevBigBlindSeat, {
    maxSeats: config.maxSeats,
    prevSmallBlindSeat: input.prevSmallBlindSeat,
    ...(input.freshTable === true ? { freshTable: true } : {}),
  })
  if (!assigned.ok) return assigned
  const a = assigned.value

  const snapshots = new Map<SeatIndex, TableSeatSnapshot>()
  for (const s of input.seats) snapshots.set(s.seat, s)

  // §4.15 底牌：两轮，每轮按 dealtSeats 顺序。回放确定性依赖于此顺序，不得更改。
  let deckIndex = 0
  const firstCard: Record<SeatIndex, CardId> = {}
  const secondCard: Record<SeatIndex, CardId> = {}
  for (const seat of a.dealtSeats) firstCard[seat] = input.deck[deckIndex++] as CardId
  for (const seat of a.dealtSeats) secondCard[seat] = input.deck[deckIndex++] as CardId

  const players: Record<SeatIndex, HandPlayer> = {}
  for (const seat of a.dealtSeats) {
    const snap = snapshots.get(seat)
    if (snap === undefined || snap.accountId === null) {
      return engineErr('INVALID_CONFIG', `发牌座位 ${seat} 没有玩家`, seat)
    }
    if (!Number.isInteger(snap.stack) || snap.stack < 1) {
      return engineErr('INVALID_CONFIG', `座位 ${seat} 的筹码必须 >= 1`, seat)
    }
    players[seat] = {
      seat,
      accountId: snap.accountId,
      startingStack: snap.stack,
      stack: snap.stack,
      holeCards: [firstCard[seat] as CardId, secondCard[seat] as CardId],
      status: 'active',
      committedThisRound: 0,
      committedThisHand: 0,
      hasActedThisRound: false,
      lastAction: null,
      hasShownCards: false,
    }
  }

  const draft: EngineHandState = {
    schemaVersion: HAND_STATE_SCHEMA_VERSION,
    config,
    handNo: input.handNo ?? 0,
    buttonSeat: a.buttonSeat,
    smallBlindSeat: a.smallBlindSeat,
    bigBlindSeat: a.bigBlindSeat,
    smallBlindPosition: a.smallBlindPosition,
    isHeadsUp: a.isHeadsUp,
    deadButton: a.deadButton,
    dealtSeats: a.dealtSeats,
    players,
    // 防御性拷贝：入参数组的所有权在调用方手里，引擎不能把自己的确定性押在它不被改写上
    deck: input.deck.slice(),
    deckIndex,
    burned: [],
    board: [],
    phase: 'dealing',
    street: null,
    betToMatch: 0,
    lastFullRaiseSize: config.bigBlind,
    lastAggressorSeatThisStreet: null,
    actionOn: null,
    potTotal: 0,
    pots: [],
    actionLog: [],
    uncalledReturn: null,
    showdown: [],
    result: null,
  }

  const events: HandEvent[] = []
  // 位置标签按实际盲注座位派发，不能从下标反推：dead small blind / dead button 时
  // 下标推断会整体错位一格，与本事件自己给出的 buttonSeat / sbSeat 打架。
  const preflopOrder = seatsClockwiseFrom(a.bigBlindSeat + 1, config.maxSeats).filter(
    (s) => players[s] !== undefined,
  )
  const positions = positionsForHand(a, preflopOrder)
  events.push({
    t: 'handStarted',
    handId: config.handId,
    handNo: draft.handNo,
    buttonSeat: a.buttonSeat,
    sbSeat: a.smallBlindSeat,
    bbSeat: a.bigBlindSeat,
    deadButton: a.deadButton,
    players: preflopOrder.map((seat) => {
      const p = seatOf(draft, seat)
      return {
        seat,
        accountId: p.accountId,
        stack: p.startingStack,
        position: positions.get(seat)!,
      }
    }),
  })

  // §4.9 dealing ③ ante → ④ 小盲 + 大盲 + 补盲（活大盲计入本轮，死小盲不计入）
  //   补盲者先收活大盲、再收死小盲：筹码不够时活钱优先（只交了死钱就全下的人只能争死钱那一层）
  if (config.anteMode === 'each' && config.ante > 0) {
    for (const seat of a.dealtSeats) {
      postForced(draft, seat, 'postAnte', 'ante', config.ante, events, false)
    }
  } else if (config.anteMode === 'bigBlindAnte' && config.ante > 0) {
    postForced(draft, a.bigBlindSeat, 'postAnte', 'ante', config.ante, events, false)
  }
  if (a.smallBlindSeat !== null) {
    postForced(draft, a.smallBlindSeat, 'postSmallBlind', 'sb', config.smallBlind, events, true)
  }
  postForced(draft, a.bigBlindSeat, 'postBigBlind', 'bb', config.bigBlind, events, true)
  // 补盲的活大盲沿用 postBigBlind / 'bb'：算本人翻前下注、不置 hasActedThisRound，轮到他照样有 option。
  // 「谁是本手大盲」一律读 bigBlindSeat，不看动作类型（统计、回放、客户端都如此）
  for (const seat of a.livePostSeats) {
    postForced(draft, seat, 'postBigBlind', 'bb', config.bigBlind, events, true)
  }
  for (const seat of a.deadSmallBlindSeats) {
    postForced(draft, seat, 'postDead', 'dead', config.smallBlind, events, false)
  }

  events.push({
    t: 'holeCardsDealt',
    cardsBySeat: Object.fromEntries(
      a.dealtSeats.map((seat) => [seat, seatOf(draft, seat).holeCards]),
    ) as Readonly<Record<SeatIndex, readonly [CardId, CardId]>>,
  })

  // ⑥ + preflop 入口：openBettingRound 内含 resetBettingRound，betToMatch 回到 bigBlind
  openBettingRound(draft, 'preflop')
  // ⑦ 立即跑一次结束判定——ante / 盲注完全可能已经把所有人打成全下
  advance(draft, events)

  return engineOk({ state: draft, events })
}

// ============================================================================
// applyAction（§4.6 / §4.14）
// ============================================================================

function applyToDraft(draft: HandState, action: AppliedAction): void {
  const p = seatOf(draft, action.seat)
  const prevBetToMatch = draft.betToMatch

  if (action.delta > 0) {
    p.stack -= action.delta
    p.committedThisRound += action.delta
    p.committedThisHand += action.delta
  }

  switch (action.type) {
    case 'fold':
      p.status = 'folded'
      break
    case 'check':
    case 'call':
      // 跟注（含全下不足额跟注）**不改变 betToMatch**，这正是边池的来源
      break
    case 'bet':
    case 'raise':
      draft.betToMatch = action.to
      draft.lastAggressorSeatThisStreet = action.seat
      if (action.reopenedAction) {
        draft.lastFullRaiseSize = action.to - prevBetToMatch
        // 只有「完整」下注/加注才重开加注权；不完整全下**不**重置任何人的标志
        for (const o of playersOf(draft)) {
          if (o.seat !== action.seat) o.hasActedThisRound = false
        }
      }
      break
    default:
      throw new InvariantError(`applyToDraft: ${action.type} 不是玩家动作`)
  }

  if (p.stack === 0 && p.status !== 'folded') p.status = 'allIn'
  p.hasActedThisRound = true
  p.lastAction = action.type
  draft.actionLog.push(action)
  syncPotTotal(draft)
}

export function applyAction(state: HandState, req: ActionRequest): EngineResult<ActionResult> {
  const validated = validateAction(state, req)
  if (!validated.ok) return validated

  // 幂等重放：validateAction 已确认 (seat, intent) 与已落库条目一致 → 状态不变、事件为空。
  // 这个分支必须在 clone 之前短路，否则重发的包会被当成新动作再打一遍。
  if (req.seq < state.actionLog.length) return engineOk({ state, events: [] })

  const action = validated.value
  const draft = cloneState(state)
  const events: HandEvent[] = []

  applyToDraft(draft, action)
  events.push({ t: 'playerActed', action })
  draft.actionOn = nextToAct(draft, action.seat + 1)
  advance(draft, events)

  return engineOk({ state: draft, events })
}

// ============================================================================
// replayHand（§4.14 seq 索引契约）
// ============================================================================

/**
 * `actionLog` 含盲注 / ante 条目，因此 `ActionRequest[]` 的数组下标与 `seq` **不相等**。
 * 故本函数**忽略入参 `seq` 与 `handId`**，一律用当前 `actionLog.length` 与本手 handId 重填——
 * 持久化时只需存 `(seat, intent, source)` 三元组。
 *
 * 任一步失败即回放失败（这是数据损坏的信号，不是可恢复的用户错误）。
 */
export function replayHand(
  input: StartHandInput,
  actions: readonly ActionRequest[],
): EngineResult<HandState> {
  const started = startHand(input)
  if (!started.ok) return started
  let st = started.value.state

  for (const a of actions) {
    const r = applyAction(st, {
      handId: input.config.handId as HandId,
      seat: a.seat,
      intent: a.intent,
      seq: st.actionLog.length,
      source: a.source,
    })
    if (!r.ok) return r
    st = r.value.state
  }
  return engineOk(st)
}
